import { useState, useEffect, useCallback, useRef } from 'react';
import { detectCardType, formatCardNumber, validateCardNumber } from '../utils/cardDetection';

/**
 * Custom hook for handling credit card input with real-time detection
 * @param {string} initialValue - Initial card number value
 * @param {function} onChange - Callback when card number changes
 * @returns {object} - Hook state and handlers
 */
export const useCardInput = (initialValue = '', onChange = null) => {
  const [cardNumber, setCardNumber] = useState(initialValue);
  const [detectedCardType, setDetectedCardType] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [formattedNumber, setFormattedNumber] = useState('');

  // Keep the latest onChange in a ref rather than the effect's dependency
  // array below - callers commonly pass a fresh inline arrow function on
  // every render (as PaymentLinkPage.js does), which would otherwise make
  // the effect re-fire every render it itself triggers via setState,
  // producing an infinite "Maximum update depth exceeded" render loop.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Update card detection when card number changes
  useEffect(() => {
    const detected = detectCardType(cardNumber);
    setDetectedCardType(detected);
    
    // Format the card number
    const formatted = formatCardNumber(cardNumber, detected?.type);
    setFormattedNumber(formatted);
    
    // Validate card number
    const valid = cardNumber.length >= 13 && validateCardNumber(cardNumber);
    setIsValid(valid);
    
    // Call external onChange if provided
    if (onChangeRef.current) {
      onChangeRef.current({
        cardNumber,
        detectedCardType: detected,
        isValid: valid,
        formattedNumber: formatted
      });
    }
  }, [cardNumber]);

  // Handle input change with formatting
  const handleCardNumberChange = useCallback((event) => {
    const value = event.target.value;
    // Remove all non-numeric characters
    const cleanValue = value.replace(/\D/g, '');
    
    // Limit to maximum card number length
    const maxLength = 19;
    const truncatedValue = cleanValue.substring(0, maxLength);
    
    setCardNumber(truncatedValue);
  }, []);

  // Handle paste events
  const handlePaste = useCallback((event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const cleanValue = pastedText.replace(/\D/g, '');
    const truncatedValue = cleanValue.substring(0, 19);
    
    setCardNumber(truncatedValue);
  }, []);

  // Get input props for easy integration
  const getInputProps = useCallback(() => ({
    value: formattedNumber,
    onChange: handleCardNumberChange,
    onPaste: handlePaste,
    placeholder: 'Card Number',
    type: 'text',
    inputMode: 'numeric',
    autoComplete: 'cc-number',
    maxLength: 23, // Formatted length with spaces
    'aria-label': 'Credit card number',
    'data-card-type': detectedCardType?.type || ''
  }), [formattedNumber, handleCardNumberChange, handlePaste, detectedCardType]);

  // Reset function
  const reset = useCallback(() => {
    setCardNumber('');
    setDetectedCardType(null);
    setIsValid(false);
    setFormattedNumber('');
  }, []);

  // Set card number programmatically
  const setCardNumberValue = useCallback((value) => {
    const cleanValue = value.replace(/\D/g, '');
    setCardNumber(cleanValue);
  }, []);

  return {
    cardNumber,
    detectedCardType,
    isValid,
    formattedNumber,
    handleCardNumberChange,
    handlePaste,
    getInputProps,
    reset,
    setCardNumber: setCardNumberValue
  };
};

/**
 * Custom hook for CVV input with card type awareness
 * @param {object} cardType - Detected card type object
 * @param {string} initialValue - Initial CVV value
 * @returns {object} - CVV input state and handlers
 */
export const useCVVInput = (cardType = null, initialValue = '') => {
  const [cvv, setCvv] = useState(initialValue);
  const [isValid, setIsValid] = useState(false);

  // Get expected CVV length based on card type
  const expectedLength = cardType?.cvvLength || 3;

  useEffect(() => {
    setIsValid(cvv.length === expectedLength && /^\d+$/.test(cvv));
  }, [cvv, expectedLength]);

  const handleCVVChange = useCallback((event) => {
    const value = event.target.value.replace(/\D/g, '');
    const maxLength = cardType?.cvvLength || 4; // Max 4 for safety
    setCvv(value.substring(0, maxLength));
  }, [cardType]);

  const getCVVInputProps = useCallback(() => ({
    value: cvv,
    onChange: handleCVVChange,
    placeholder: cardType?.type === 'amex' ? 'CVV (4 digits)' : 'CVV',
    type: 'text',
    inputMode: 'numeric',
    autoComplete: 'cc-csc',
    maxLength: expectedLength,
    'aria-label': 'Card security code',
    'data-expected-length': expectedLength
  }), [cvv, handleCVVChange, cardType, expectedLength]);

  const reset = useCallback(() => {
    setCvv('');
    setIsValid(false);
  }, []);

  return {
    cvv,
    isValid,
    expectedLength,
    handleCVVChange,
    getCVVInputProps,
    reset,
    setCvv
  };
};

/**
 * Custom hook for expiry date input
 * @param {string} initialValue - Initial expiry value (MM/YY format)
 * @returns {object} - Expiry input state and handlers
 */
export const useExpiryInput = (initialValue = '') => {
  const [expiry, setExpiry] = useState(initialValue);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    // Validate MM/YY format and check if date is in the future
    const isValidFormat = /^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry);
    
    if (isValidFormat) {
      const [month, year] = expiry.split('/');
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear() % 100; // Get last 2 digits
      const currentMonth = currentDate.getMonth() + 1;
      
      const expiryYear = parseInt(year, 10);
      const expiryMonth = parseInt(month, 10);
      
      // Check if expiry date is in the future
      const isFuture = expiryYear > currentYear || 
                      (expiryYear === currentYear && expiryMonth >= currentMonth);
      
      setIsValid(isFuture);
    } else {
      setIsValid(false);
    }
  }, [expiry]);

  const handleExpiryChange = useCallback((event) => {
    let value = event.target.value.replace(/\D/g, '');
    
    // Auto-format MM/YY
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4);
    }
    
    setExpiry(value);
  }, []);

  const getExpiryInputProps = useCallback(() => ({
    value: expiry,
    onChange: handleExpiryChange,
    placeholder: 'MM/YY',
    type: 'text',
    inputMode: 'numeric',
    autoComplete: 'cc-exp',
    maxLength: 5,
    'aria-label': 'Card expiry date'
  }), [expiry, handleExpiryChange]);

  const reset = useCallback(() => {
    setExpiry('');
    setIsValid(false);
  }, []);

  return {
    expiry,
    isValid,
    handleExpiryChange,
    getExpiryInputProps,
    reset,
    setExpiry
  };
};
